import React, { useEffect, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';

import type { Offer } from '@luxuryescapes/contract-svc-offer';

import { useGetExperienceDealOptions } from '~/hooks/apis/offers/useGetExperienceDealOptions';

import { formatNumber } from '~/utils/numberFormatters';
import { pluralizeToString } from '~/utils/stringUtils';

import CheckboxList from './CheckboxList';
import { mergeSelectionById } from './packageOptionsModel';
import { AttachedExperience } from './usePackageOptionsState';

const EXPERIENCE_PAGE_SIZE_OPTIONS = [10, 25, 50];
const INITIAL_PAGINATION: GridPaginationModel = { page: 0, pageSize: 10 };

export type BundledExperiencesResult = {
  assetIds: Array<string>;
  optionIndexes: Array<number>;
  sellIncrease: number;
  netIncrease: number;
  propertySellIncrease: number;
  suffix: string;
  experiences: Array<AttachedExperience>;
};

type BundledExperiencesWizardProps = {
  offerCountry?: string | null;
  destination?: string | null;
  subDestination?: string | null;
  travelFrom?: string | null;
  baseRoomAssetIds: Array<string>;
  baseRoomLabel: (assetId: string) => string;
  initialAssets: Array<string>;
  initialPropertyNet: string;
  initialPropertySell: string;
  netCurrency: string;
  initialSuffix: string;
  onApply: (result: BundledExperiencesResult) => void;
  onClose: () => void;
  open: boolean;
  optionLabels: Array<string>;
};

const EMPTY_CELL = '-';

const formatEmptyCell = (value: unknown) => (value == null || value === '' ? EMPTY_CELL : value);

const BASE_EXPERIENCE_COLUMNS: Array<GridColDef<Offer.ExperienceDealOption>> = [
  {
    field: 'luxplus_exclusive',
    headerName: 'LuxPlus',
    width: 90,
    valueFormatter: (value: boolean) => (value ? 'Yes' : 'No'),
  },
  { field: 'travel_to', headerName: 'Travel To', width: 120 },
  { field: 'account_name', headerName: 'Account Name', width: 180 },
  { field: 'name', headerName: 'Deal Option Name', width: 320 },
  { field: 'fare_type', headerName: 'Fare Type', width: 110 },
  { field: 'ac_deal_category', headerName: 'AC Deal Category', width: 150 },
  { field: 'ac_deal_sub_category', headerName: 'Sub Category', width: 150 },
  {
    field: 'ancillary_sell_uplift',
    headerName: 'Ancillary Sell Price Uplift',
    align: 'right',
    headerAlign: 'right',
    width: 180,
  },
  { field: 'cost_price', headerName: 'Value (Cost Currency)', align: 'right', headerAlign: 'right', width: 170 },
  { field: 'currency', headerName: 'Currency', width: 130 },
  { field: 'destination', headerName: 'Destination', width: 140 },
  { field: 'sub_destination', headerName: 'Sub-Destination', width: 150 },
];

// Spread last so a column's own valueFormatter (LuxPlus Yes/No) overrides the default.
const EXPERIENCE_COLUMNS = BASE_EXPERIENCE_COLUMNS.map((column) => ({ valueFormatter: formatEmptyCell, ...column }));

export default function BundledExperiencesWizard({
  offerCountry,
  destination,
  subDestination,
  travelFrom,
  baseRoomAssetIds,
  baseRoomLabel,
  initialAssets,
  initialPropertyNet,
  initialPropertySell,
  initialSuffix,
  netCurrency,
  onApply,
  onClose,
  open,
  optionLabels,
}: BundledExperiencesWizardProps) {
  const optionIndexes = optionLabels.map((_label, index) => index);

  const [step, setStep] = useState(1);
  const [selectedExperiencesById, setSelectedExperiencesById] = useState<Record<string, Offer.ExperienceDealOption>>(
    {},
  );
  const [selectedAssets, setSelectedAssets] = useState<Array<string>>([]);
  const [selectedOptions, setSelectedOptions] = useState<Array<number>>(optionIndexes);
  const [suffix, setSuffix] = useState(initialSuffix);
  const [alsoPropertyNet, setAlsoPropertyNet] = useState(false);
  const [propertySell, setPropertySell] = useState('');
  const [propertyNet, setPropertyNet] = useState('');
  const [experienceSearch, setExperienceSearch] = useState('');
  const [submittedSearch, setSubmittedSearch] = useState('');
  const [paginationModel, setPaginationModel] = useState(INITIAL_PAGINATION);

  const {
    data: experienceDealOptions,
    error: experienceDealOptionsError,
    isFetching: isFetchingExperiences,
  } = useGetExperienceDealOptions(
    {
      deal_sub_category: offerCountry ?? '',
      destination,
      sub_destination: subDestination,
      travel_from: travelFrom,
      search: submittedSearch,
      page: paginationModel.page + 1,
      limit: paginationModel.pageSize,
    },
    { enabled: open && step === 1 },
  );

  useEffect(() => {
    if (!open) {
      return;
    }
    const carriesPropertyUplift = initialPropertySell !== '' || initialPropertyNet !== '';
    setStep(1);
    setSelectedExperiencesById({});
    setSelectedAssets(carriesPropertyUplift ? initialAssets : []);
    setSelectedOptions(optionLabels.map((_label, index) => index));
    setSuffix(initialSuffix);
    setAlsoPropertyNet(carriesPropertyUplift);
    setPropertySell(initialPropertySell);
    setPropertyNet(initialPropertyNet);
    setExperienceSearch('');
    setSubmittedSearch('');
    setPaginationModel(INITIAL_PAGINATION);
    // Keyed on `open` only: re-running on card changes would discard in-dialog edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const visibleExperiences = experienceDealOptions?.experience_deal_options ?? [];
  const selectedExperienceIds = Object.keys(selectedExperiencesById);
  const selectedExperiences = Object.values(selectedExperiencesById);
  const experienceSellTotal = selectedExperiences.reduce(
    (total, experience) => total + (experience.ancillary_sell_uplift ?? 0),
    0,
  );
  const experienceCostTotal = selectedExperiences.reduce(
    (total, experience) => total + (experience.converted_cost_aud ?? 0),
    0,
  );
  const experiencesMissingPricing = selectedExperiences.filter(
    (experience) => experience.ancillary_sell_uplift == null || experience.converted_cost_aud == null,
  );

  const matchedFilters = [
    'offer country',
    destination && 'destination',
    subDestination && 'sub-destination',
    travelFrom && 'travel-from date',
  ]
    .filter(Boolean)
    .join(', ');

  function handleSearch(event: React.FormEvent) {
    event.preventDefault();
    setSubmittedSearch(experienceSearch.trim());
    setPaginationModel({ ...paginationModel, page: 0 });
  }

  function handleExperienceSelectionChange(ids: Array<string>) {
    setSelectedExperiencesById(mergeSelectionById(ids, selectedExperiencesById, visibleExperiences));
  }
  const propertySellValue = alsoPropertyNet ? Number(propertySell) || 0 : 0;
  const propertyNetValue = alsoPropertyNet ? Number(propertyNet) || 0 : 0;

  function handleNext() {
    if (step === 3) {
      onApply({
        assetIds: selectedAssets,
        optionIndexes: selectedOptions,
        sellIncrease: experienceSellTotal + propertySellValue,
        netIncrease: propertyNetValue,
        propertySellIncrease: propertySellValue,
        suffix: suffix.trim(),
        experiences: selectedExperiences.map((experience) => ({
          id: experience.id,
          dealOptionName: experience.name ?? '',
          ancillarySellPriceUplift: experience.ancillary_sell_uplift ?? 0,
          valueCostCurrency: experience.cost_price ?? 0,
          currency: experience.currency ?? '',
          costAud: experience.converted_cost_aud ?? 0,
        })),
      });
      onClose();
      return;
    }
    if (step === 1) {
      setSelectedAssets([...baseRoomAssetIds]);
    }
    setStep(step + 1);
  }

  const nextDisabled =
    (step === 1 && selectedExperienceIds.length === 0) ||
    (step === 2 && (selectedAssets.length === 0 || selectedOptions.length === 0)) ||
    (step === 3 && !suffix.trim());

  return (
    <Dialog fullWidth maxWidth="lg" onClose={onClose} open={open}>
      <DialogTitle>
        {step === 1 && 'Select bundled experiences'}
        {step === 2 && 'Apply to room types'}
        {step === 3 && 'Finalize upgrade package'}
      </DialogTitle>
      <DialogContent dividers>
        {step === 1 && (
          <>
            <Typography color="text.secondary" variant="body2">
              Please select the experiences you want to add to your deal options.
              {offerCountry && ` Showing experiences matching this property's ${matchedFilters}.`}
            </Typography>
            <Stack component="form" direction="row" mt={2} onSubmit={handleSearch} spacing={2}>
              <TextField
                fullWidth
                label="Search deal option name"
                onChange={(e) => setExperienceSearch(e.target.value)}
                placeholder="e.g. Transfer to the airport"
                size="small"
                value={experienceSearch}
              />
              <Button type="submit" variant="contained">
                Search
              </Button>
            </Stack>
            {!offerCountry && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Set an offer country in Property Setup to see experiences.
              </Alert>
            )}
            {experienceDealOptionsError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                Failed to load experiences: {experienceDealOptionsError.message}
              </Alert>
            )}
            <Box mt={2}>
              <DataGrid
                autoHeight
                checkboxSelection
                columns={EXPERIENCE_COLUMNS}
                disableRowSelectionOnClick
                keepNonExistentRowsSelected
                loading={isFetchingExperiences}
                onPaginationModelChange={setPaginationModel}
                onRowSelectionModelChange={(model) => handleExperienceSelectionChange(model as Array<string>)}
                pageSizeOptions={EXPERIENCE_PAGE_SIZE_OPTIONS}
                paginationMode="server"
                paginationModel={paginationModel}
                rowCount={experienceDealOptions?.total ?? 0}
                rowSelectionModel={selectedExperienceIds}
                rows={visibleExperiences}
              />
            </Box>
          </>
        )}

        {step === 2 && (
          <>
            <Typography color="text.secondary" variant="body2">
              Choose which base room types should receive the upgrade. Packages will be created as{' '}
              <Typography component="span" fontStyle="italic" variant="inherit">
                {suffix.trim() || 'Suffix'}
              </Typography>{' '}
              across the selected options.
            </Typography>
            <Box bgcolor="action.hover" borderRadius={1} mt={2} p={1.5}>
              <Typography variant="body2">
                Selected: {pluralizeToString('experience', selectedExperiences.length)} · Experience sell +$
                {formatNumber(experienceSellTotal)} · Experience cost ~$
                {formatNumber(experienceCostTotal)} AUD (tracked separately — package net unchanged unless you add a
                property uplift next)
              </Typography>
            </Box>
            {experiencesMissingPricing.length > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Missing sell uplift or AUD cost, counted as $0:{' '}
                {experiencesMissingPricing.map((experience) => experience.name ?? experience.id).join(', ')}
              </Alert>
            )}

            <Box display="grid" gap={2} gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} mt={2}>
              <CheckboxList
                emptyMessage="No base room types found — add rooms in Package Options first."
                items={baseRoomAssetIds}
                label={baseRoomLabel}
                onChange={setSelectedAssets}
                selected={selectedAssets}
                title="Apply to base room types"
              />
              <CheckboxList
                items={optionIndexes}
                label={(index) => optionLabels[index]}
                onChange={setSelectedOptions}
                selected={selectedOptions}
                title="Package options"
              />
            </Box>
          </>
        )}

        {step === 3 && (
          <>
            <Typography color="text.secondary" variant="body2">
              Confirm the package name and pricing. Sell increases by the property sell uplift plus experience sell;
              package net increases by the property net uplift only (experience cost is tracked separately).
            </Typography>

            <TextField
              fullWidth
              helperText="Appended as Room - Suffix"
              label="Suffix"
              onChange={(e) => setSuffix(e.target.value)}
              placeholder="e.g. Indulgence Package + Transfers"
              size="small"
              sx={{ mt: 2 }}
              value={suffix}
            />

            <Box border={1} borderColor="divider" borderRadius={1} mt={2} p={1.5}>
              <FormControlLabel
                control={<Checkbox checked={alsoPropertyNet} onChange={(e) => setAlsoPropertyNet(e.target.checked)} />}
                label="Yes — also increase property sell / net on this package"
              />
              {alsoPropertyNet && (
                <Box display="grid" gap={2} gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} mt={1}>
                  <TextField
                    inputProps={{ min: 0 }}
                    label="Sell price increase (AUD)"
                    onChange={(e) => setPropertySell(e.target.value)}
                    placeholder="e.g. 200"
                    size="small"
                    type="number"
                    value={propertySell}
                  />
                  <TextField
                    inputProps={{ min: 0 }}
                    label={`Net rate increase (${netCurrency})`}
                    onChange={(e) => setPropertyNet(e.target.value)}
                    placeholder="e.g. 150"
                    size="small"
                    type="number"
                    value={propertyNet}
                  />
                </Box>
              )}
            </Box>

            <Box bgcolor="action.hover" borderRadius={1} mt={2} p={1.5}>
              <Typography variant="body2">
                {pluralizeToString('experience', selectedExperiences.length)} · Experience sell +$
                {formatNumber(experienceSellTotal)} · Experience cost ~${formatNumber(experienceCostTotal)} AUD (not
                added to package net)
              </Typography>
              <Typography variant="body2">
                Estimated total sell uplift +${formatNumber(experienceSellTotal + propertySellValue)} · Package net
                uplift +{netCurrency} {formatNumber(propertyNetValue)}
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Typography color="text.secondary" sx={{ mr: 'auto' }} variant="body2">
          Step {step} of 3
        </Typography>
        {step > 1 && (
          <Button onClick={() => setStep(step - 1)} variant="text">
            Back
          </Button>
        )}
        <Button onClick={onClose} variant="text">
          Cancel
        </Button>
        <Button disabled={nextDisabled} onClick={handleNext} variant="contained">
          {step === 3 ? 'Create upgrade packages' : 'Next'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
